import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { usePostHog } from "posthog-js/react";
import { stories } from "../../../utils/dataUtils";
import Sidebar from "./Sidebar";
import StoryCard from "./StoryCard";
import PersonaResult from "./PersonaResult";
import LoginPrompt from "./LoginPrompt";
import { questionsData } from '../../../data/questionsData';

// Main Quiz Component
const PsychometricTest = ({ userId }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [allResponses, setAllResponses] = useState({});
  const [showSummary, setShowSummary] = useState(false);
  const [showQuestions, setShowQuestions] = useState(false);
  const [completedStories, setCompletedStories] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const navigate = useNavigate();
  const posthog = usePostHog();

  const apiBaseURL = process.env.REACT_APP_BACKEND_API_URL;

  const handleUserLogin = async () => {
    const tempUserId = localStorage.getItem("temp_user_id");
    const userId = "fake-user-id"; // Replace with actual user ID after auth

    try {
      await fetch(`${apiBaseURL}/quiz/link-user`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          temp_user_id: tempUserId,
          user_id: userId,
        }),
      });

      // Track user login and account linking
      posthog?.capture("user_logged_in", {
        temp_user_id: tempUserId,
        user_id: userId,
        context: "post_quiz_completion",
      });

      setShowLoginPrompt(false);
      setShowSummary(true);
    } catch (err) {
      console.error("Failed to link user:", err);
      posthog?.capture("user_login_error", {
        error: err.message,
        temp_user_id: tempUserId,
      });
    }
  };

useEffect(() => {
  const tempUserId = localStorage.getItem("temp_user_id");
  const isRetake = new URLSearchParams(window.location.search).get("retake") === "true";

  const startQuiz = async () => {
  const res = await fetch(`${apiBaseURL}/quiz/start`, { method: "POST" });
  const data = await res.json();
  const tempUserId = data.temp_user_id;

  localStorage.setItem("temp_user_id", tempUserId);

  const user = JSON.parse(localStorage.getItem("user"));
  if (user?.user_id) {
    try {
      await fetch(`${apiBaseURL}/quiz/link-user`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          temp_user_id: tempUserId,
          user_id: user.user_id,
        }),
      });

      posthog?.capture("user_linked_preemptively", {
        user_id: user.user_id,
        temp_user_id: tempUserId,
        context: "quiz_start",
      });
    } catch (err) {
      console.error("Failed to link user early:", err);
    }
  }

  posthog?.capture("quiz_started", {
    temp_user_id: tempUserId,
    user_id: user?.user_id || null,
  });
};


  const fetchProgress = async () => {
  try {
    const res = await fetch(`${apiBaseURL}/quiz/progress/${tempUserId}`);
    if (!res.ok) return;

    const data = await res.json();

    if (data.submitted === true) {
      navigate("/quiz/result", {
        state: { result_persona: data.result_persona || "F" },
      });
      return;
    }

    setCurrentPage(data.current_page);
    setCompletedStories(data.completed_story_ids);
    setAllResponses(data.all_responses);

    const currentStory = stories[data.current_page - 1];
    const storyResponses = data.all_responses?.[currentStory.id] || {};
    const isComplete = currentStory.questions.every((q) => storyResponses[q.id]);

    setShowQuestions(isComplete); 

    posthog?.capture("quiz_resumed", {
      temp_user_id: tempUserId,
      current_page: data.current_page,
      completed_stories: data.completed_story_ids.length,
    });
  } catch (err) {
    console.error("Failed to load quiz progress:", err);
    posthog?.capture("quiz_progress_load_error", {
      error: err.message,
      temp_user_id: tempUserId,
    });
  }
};


  if (isRetake) {
    startQuiz(); // force fresh session
  } else if (!tempUserId) {
    startQuiz(); // first-time
  } else {
    fetchProgress(); // resume existing
  }

  posthog?.capture("psychometric_test_loaded");
}, [apiBaseURL, posthog, userId, navigate]);


  const totalPages = stories.length;
  const currentStory = stories[currentPage - 1];

  const handleNext = async () => {
  if (!showQuestions) {
    setShowQuestions(true);

    posthog?.capture("story_read", {
      story_id: currentStory.id,
      story_title: currentStory.title,
      page: currentPage,
    });
  } else if (currentPage < totalPages) {
    if (!completedStories.includes(currentStory.id)) {
      setCompletedStories((prev) => [...prev, currentStory.id]);
    }

    setCurrentPage((prev) => prev + 1);
    setShowQuestions(false);

    posthog?.capture("story_completed", {
      story_id: currentStory.id,
      story_title: currentStory.title,
      page: currentPage,
      total_completed: completedStories.length + 1,
    });
  } else {
    if (!completedStories.includes(currentStory.id)) {
      setCompletedStories((prev) => [...prev, currentStory.id]);
    }

    const topPersona = calculatePersonaScores();

    const tempUserId = localStorage.getItem("temp_user_id");

    try {
      await fetch(`${apiBaseURL}/quiz/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          temp_user_id: tempUserId,
          result_persona: topPersona,
        }),
      });

      posthog?.capture("quiz_completed", {
        temp_user_id: tempUserId,
        result_persona: topPersona,
        total_stories: totalPages,
      });
    } catch (err) {
      console.error("Quiz submission failed:", err);
      posthog?.capture("quiz_submission_error", {
        error: err.message,
        temp_user_id: tempUserId,
      });
    }

    // ✅ Clean redirect logic
    const user = JSON.parse(localStorage.getItem("user"));

    if (user?.user_id) {
      navigate("/quiz/result", {
        state: { result_persona: topPersona },
      });
    } else {
      const redirectURL = `/quiz/result?persona=${topPersona}`;
      navigate(`/signUp?redirect=${encodeURIComponent(redirectURL)}`, {
        state: { fromQuiz: true },
      });
    }
  }

  setSidebarOpen(false);
};


  const handlePrev = () => {
    if (showQuestions) {
      // Go back to story view
      setShowQuestions(false);

      // Track navigation back to story
      posthog?.capture("navigation_back_to_story", {
        story_id: currentStory.id,
        story_title: currentStory.title,
        page: currentPage,
      });
    } else if (currentPage > 1) {
      // Go to previous story and show its questions
      setCurrentPage((prev) => prev - 1);
      setShowQuestions(true);

      // Track navigation to previous story
      posthog?.capture("navigation_previous_story", {
        from_page: currentPage,
        to_page: currentPage - 1,
        navigation_method: "button",
      });
    }
    setSidebarOpen(false); // Close sidebar on mobile after navigation
  };

  const handleStoryResponse = (storyId, responses) => {
      const updatedResponses = {
    ...allResponses,
    [storyId]: {
      ...allResponses[storyId], // preserve prior answers within the same story
      ...responses,             // update only what's changed
    },
  };

    setAllResponses(updatedResponses);

    // Track answer submission
    const currentStory = stories.find((s) => s.id === storyId);
    const answeredQuestions = Object.keys(responses).length;
    const totalQuestions = currentStory?.questions?.length || 0;

    posthog?.capture("question_answered", {
      story_id: storyId,
      story_title: currentStory?.title,
      answered_questions: answeredQuestions,
      total_questions: totalQuestions,
      is_story_complete: answeredQuestions === totalQuestions,
      responses: Object.keys(responses),
    });

    const saveProgress = async () => {
      const tempUserId = localStorage.getItem("temp_user_id");
      if (!tempUserId) return;

      try {
        await fetch(`${apiBaseURL}/quiz/progress`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            temp_user_id: tempUserId,
            current_page: currentPage,
            completed_story_ids: completedStories,
            all_responses: updatedResponses,
          }),
        });

        // Track progress save
        posthog?.capture("progress_saved", {
          temp_user_id: tempUserId,
          current_page: currentPage,
          total_responses: Object.keys(updatedResponses).length,
        });
      } catch (err) {
        console.error("Failed to save progress:", err);
        posthog?.capture("progress_save_error", {
          error: err.message,
          temp_user_id: tempUserId,
        });
      }
    };

    saveProgress();
  };

  const handleStorySelect = (storyId) => {
    const storyIndex = stories.findIndex((s) => s.id === storyId);
    if (storyIndex !== -1) {
      const selectedStory = stories[storyIndex];
      setCurrentPage(storyIndex + 1);
      setShowQuestions(false);
      setSidebarOpen(false); // Close sidebar on mobile after selection

      // Track story navigation
      posthog?.capture("story_navigated", {
        from_story_id: currentStory.id,
        to_story_id: storyId,
        to_story_title: selectedStory.title,
        navigation_method: "sidebar",
      });
    }
  };

const calculatePersonaScores = () => {
  const personaScores = { A: 0, B: 0, C: 0, D: 0, E: 0, F: 0 };

  Object.entries(allResponses).forEach(([storyId, storyResponses]) => {
    // Get the story object by ID from the frontend questionsData
    const story = questionsData.find((s) => s.storyId === parseInt(storyId));
    if (!story) return;

    Object.entries(storyResponses).forEach(([questionId, selectedOption]) => {
      // Find the corresponding question by ID to get the marks
      const question = story.questions.find((q) => q.id === questionId);
      if (!question || !selectedOption) return;

      // Personas can be a single string or an array — normalize to array
      const personas = Array.isArray(selectedOption.persona)
        ? selectedOption.persona
        : [selectedOption.persona];

      // Add full `marks` (can be positive or negative) to each persona
      personas.forEach((persona) => {
        if (personaScores.hasOwnProperty(persona)) {
          personaScores[persona] += question.marks;
        }
      });
    });
  });

  // Determine the top-scoring persona (with alphabetical tiebreaker)
  let topPersona = null;
  let maxScore = -Infinity;

  Object.entries(personaScores).forEach(([persona, score]) => {
    if (score > maxScore || (score === maxScore && persona < topPersona)) {
      topPersona = persona;
      maxScore = score;
    }
  });

  return topPersona;
};


  const handleRetakeQuiz = () => {
    // Track quiz retake
    posthog?.capture("quiz_retaken", {
      previous_completed_stories: completedStories.length,
      previous_responses: Object.keys(allResponses).length,
    });

    setShowSummary(false);
    setCurrentPage(1);
    setAllResponses({});
    setShowQuestions(false);
    setCompletedStories([]);
  };

  const isCurrentStoryComplete = () => {
    const currentStory = stories[currentPage - 1];
    const storyResponses = allResponses[currentStory.id] || {};
    return currentStory.questions.every((q) => storyResponses[q.id]);
  };

  const getButtonText = () => {
    if (!showQuestions) {
      return "Tell Sto your view";
    }
    if (currentPage === totalPages && showQuestions) {
      return "Submit Quiz";
    }
    return "Next story";
  };

  const canProceed = () => {
    if (!showQuestions) return true;
    return isCurrentStoryComplete();
  };

  if (showLoginPrompt) {
    return <LoginPrompt onUserLogin={handleUserLogin} />;
  }

  if (showSummary) {
    const personaScores = calculatePersonaScores();
    return (
      <PersonaResult
        personaScores={personaScores}
        onRetakeQuiz={handleRetakeQuiz}
      />
    );
  }

  return (
    <div className="min-h-screen bg-white flex">
      {/* Sidebar */}
      <Sidebar
        stories={stories}
        currentStoryId={currentStory.id}
        completedStories={completedStories}
        onStorySelect={handleStorySelect}
        isOpen={sidebarOpen}
        onToggle={() => setSidebarOpen(!sidebarOpen)}
      />

      {/* Main Content */}
      <div className="flex-1 lg:ml-0 p-4 lg:p-8">
        {/* Mobile header space */}
        <div className="lg:hidden h-12 mb-4"></div>

        <div className="max-w-4xl mx-auto space-y-6 lg:space-y-8">
          <StoryCard
            story={currentStory}
            onSaveResponses={(responses) =>
              handleStoryResponse(currentStory.id, responses)
            }
            savedResponses={allResponses[currentStory.id] || {}}
            showQuestions={showQuestions}
          />

          <div className="flex flex-col sm:flex-row justify-center items-center gap-4 pb-8">
            {(showQuestions || currentPage > 1) && (
              <button
                onClick={handlePrev}
                className="w-full max-w-xs sm:w-auto sm:px-8 sm:py-3 px-4 py-3 bg-[#E3E3E3] hover:bg-gray-300 text-[#484848] rounded-full font-gotham font-medium transition-colors"
              >
                {showQuestions ? "Back to story" : "Previous"}
              </button>
            )}

            <button
              onClick={handleNext}
              disabled={!canProceed()}
              className={`w-full max-w-xs sm:w-auto sm:px-8 sm:py-3 px-4 py-3 rounded-full text-base transition-colors font-bold font-gotham ${
                canProceed()
                  ? "bg-[#54B0AF] hover:bg-teal-600 text-white"
                  : "bg-[#E3E3E3] cursor-not-allowed text-[#484848]"
              }`}
            >
              {getButtonText()}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PsychometricTest;
